
<template>
<div>

	<!-- Loading -->
	<div v-if="!initializationDone">
		<Preloader />
	</div>

	<!-- initializationDone -->
	<div v-else>

		<!-- Country Support Checking -->
		<div v-if="isGeoBanned">
			<v-card class="mx-auto" min-width="400" max-width="400">
				<v-card-text class="pa-7 text-center">
					<div class="text-center">
						<v-icon large color="error" class="mb-2">mdi-alert-circle-outline</v-icon>
						<div class="title">
							{{ $t('loginPage.titleLocationBanned', {country: geoChecking.country}) }}
						</div>
					</div>
					<div class="mt-2 mb-2">
						{{ $t('loginPage.txtLocationBanned', {country: geoChecking.country}) }}
					</div>
					<div v-bind:class="{'green lighten-5': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }" class="m-2 pa-3">
						<div>
								<v-icon v-if="agreeToBypassGeoBan" color="primary" @click="agreeToBypassGeoBan = !agreeToBypassGeoBan">
		            	mdi-checkbox-marked
		            </v-icon>
		            <v-icon v-else aria-hidden="true" @click="agreeToBypassGeoBan = !agreeToBypassGeoBan">
		            	mdi-checkbox-blank-outline
		            </v-icon>
								{{ $t('loginPage.txtLocationBanBypassAgreement', {country: geoChecking.country}) }}
						</div>
						<div class="text-center mt-2">
							<v-btn rounded color="primary" :disabled="!agreeToBypassGeoBan"
							@click="bypassGeoBan()">
								{{ $t('loginPage.buttonByPassLocationBan') }}
							</v-btn>
						</div>
					</div>
				</v-card-text>
			</v-card>
		</div>

		<!-- !geoBanned -->
		<div v-else>
			<!-- Wallet not available -->
			<div v-if="!walletAvailable">
				<v-card class="mx-auto" max-width="400">
					<v-card-text class="pa-7 text-center">
						<div class="text-center mb-4">
							<v-icon large color="error">mdi-alert-circle-outline</v-icon>
						</div>
						<div v-if="isMobile">
							{{ $t('loginPage.txtNeedMobileWallet') }}
						</div>
						<div v-else>
							{{ $t('loginPage.txtNeeedMataMask1') }}<a href="https://metamask.io/" target="_blank" rel="nofollow noopener noreferrer">{{ $t('loginPage.metaMaskFullName') }}</a>{{ $t('loginPage.txtNeeedMataMask2') }}
						</div>
					</v-card-text>
				</v-card>
			 </div>

			<!-- Wallet not connected -->
			<div v-if="walletAvailable && !walletConnected">
				<v-card class="mx-auto" max-width="400" :loading="loading">
					<v-card-text class="pa-7 text-center">
						<div class="headline text-center mb-3">
							<span v-if="isMobile">{{ $t('loginPage.connectWallet') }}</span>
							<span v-else>{{ $t('loginPage.connectMetaMask') }}</span>
						</div>
						<div class="mb-3">
							<span v-if="isMobile">{{ $t('loginPage.introConnectMobileWallet') }}</span>
							<span v-else>{{ $t('loginPage.introConnectMetaMask') }}</span>
						</div>
						<v-btn large rounded color="primary"
							@click="connectWallet"
							:disabled="loading"
						>
							<span v-if="isMobile">{{ $t('loginPage.connectWallet') }}</span>
							<span v-else>{{ $t('loginPage.connectMetaMask') }}</span>
							<ButtonLoading v-show="loading" class="ml-3" />
						</v-btn>
						<div v-show="error.connectWallet" class="login-error-section mt-2">
							{{ error.connectWallet }}
						</div>
					</v-card-text>

				</v-card>
			 </div>

			<!-- Network Not Supported -->
			<div v-if="walletAvailable && walletConnected && !networkSupported">
				<v-card class="mx-auto" max-width="400">
					<v-card-text class="pa-7 text-center">
						<div class="text-center mb-4">
							<v-icon large color="error">mdi-alert-circle-outline</v-icon>
						</div>
						<div>
							{{ $t('loginPage.txtWrongNetwork') }}<br />
							<v-chip v-for="(name, id) in config.supportedNetworks" :key="'networkname' + id" small class="ml-2 mt-2">{{ name }}</v-chip>
						</div>
					</v-card-text>
				</v-card>
			</div>

			 <!-- Login with wallet -->
			 <div v-if="walletAvailable && walletConnected && networkSupported">

				<v-card class="mx-auto" max-width="400" :loading="loading">

					<v-card-text class="pa-7 text-center">
						<div class="headline text-center mb-3">{{ $t('loginPage.title') }}</div>
						<div class="mb-3">
							<span v-if="isMobile">{{ $t('loginPage.txtPleaseSignWallet') }}</span>
							<span v-else>{{ $t('loginPage.txtPleaseSignMetaMask') }}</span>
						</div>

						<div class="mb-3" v-if="displayAddress">
							<v-chip
					      class="ma-2"
					      outlined
					      color="primary"
					    >
					      <v-avatar left>
					        <v-icon>mdi-account-circle</v-icon>
					      </v-avatar>
					      <UserAddress :address="displayAddress" nopopup></UserAddress>
		    			</v-chip>
						</div>

						<div class="my-2">
		          <small>
		            <v-icon v-if="agreeWithTerms" color="primary" @click="toggleAgreement">
		            	mdi-checkbox-marked
		            </v-icon>
		            <v-icon v-else aria-hidden="true" @click="toggleAgreement">
		            	mdi-checkbox-blank-outline
		            </v-icon>
		            {{ $t('loginPage.txtAgreeTermsFront') }}<a href="https://www.ledgerdex.com/terms/" target="_blank" rel="nofollow">{{ $t('loginPage.TermsAndConditions') }}</a>
		          </small>
		        </div>

						<v-btn large rounded color="primary"
							@click="logInBackend"
							:disabled="!agreeWithTerms || loading"
						>
							<span v-if="isMobile">{{ $t('loginPage.buttonTxtLogInUsingWallet') }}</span>
							<span v-else>{{ $t('loginPage.buttonTxtLogInUsingMetaMask') }}</span>
							<ButtonLoading v-show="loading" class="ml-3" />
						</v-btn>
						<div v-show="error.login" class="login-error-section mt-2">
							{{ error.login }}
						</div>

					</v-card-text>
				</v-card>

			</div>

		</div><!-- v-else: geoBanned -->

	</div><!-- v-else: initializationDone -->

</div>
</template>

<script>
import { config } from '@/config'
import Preloader from "@/components/Preloader"
import ButtonLoading from "@/components/ButtonLoading"
import UserAddress from "@/components/UserAddress"
import {mapActions, mapGetters, mapState} from 'vuex'
export default {
	components: {
		Preloader,
		ButtonLoading,
		UserAddress
	},
	data: () => ({
		config: config,
		initializationDone: false,
		geoBanned: false,
		networkSupported: false,
		geoChecking: null,
		loading: false,
		walletConnected: false,
		agreeToBypassGeoBan: false,
		agreeWithTerms: false,
		error: {
			connectWallet: null,
			login: null
		},
	}),
	computed: {
		...mapGetters({
			network: 'getNetwork',
			myAddress: 'getUserAccount',
			web3Wrapper: 'getWeb3Wrapper',
			networkConfig: 'getNetworkConfig',
		}),
		isGeoBanned: function () {
			const localKeyForBypass = config.localStorageKey.geoBanBypass
			if (localStorage.getItem(localKeyForBypass)) {
				return false
			}
			else {
				return this.geoBanned
			}
		},
		displayAddress: function () {
			if (false && this.myAddress) {
				return this.myAddress
			}
			else if (window.ethereum && window.ethereum.selectedAddress) {
				return window.ethereum.selectedAddress.toLowerCase()
			}
			else {
				return null
			}
		},
		isMobile: function () {
			return this.$vuetify.breakpoint.xsOnly
		},
		walletAvailable: function () {
			return window.ethereum
		},
 	},
	methods: {
		...mapActions(['loadNetwork', 'loadTokenInfo', 'loadAccount']),
		async init() {
			// console.log(window.ethereum)
			this.initializationDone = false
			await this.checkGeoSupport()
			this.walletConnected = this.isWalletConnected()
			this.networkSupported = this.isNetworkSupported()
			this.setupWalletListner()
			if (this.walletConnected && this.networkSupported) {
				await this.loadNetwork(true)
			}
			this.initializationDone = true
		},

		bypassGeoBan () {
			const localKeyForBypass = config.localStorageKey.geoBanBypass
			localStorage.setItem(localKeyForBypass, 'YES')
			this.geoBanned = false
		},

		setupWalletListner () {
			if (window.ethereum) {
				window.ethereum.autoRefreshOnNetworkChange = false
				const that = this
				window.ethereum.on('networkChanged', function (chainId) {
					console.log('Chain change detected.')
					that.init()
				})
				window.ethereum.on('chainChanged', function (chainId) {
					console.log('Chain change detected.')
					that.init()
				})
				window.ethereum.on('chainIdChanged', function (chainId) {
					console.log('Chain change detected.')
					that.init()
				})
				window.ethereum.on('accountsChanged', function (accounts) {
					console.log('account change detected.')
					that.init()
				})
			}
		},

		isWalletConnected() {
			return window.ethereum && window.ethereum.selectedAddress
		},

		isNetworkSupported() {
			if (this.isWalletConnected()) {
				const networkId = parseInt(window.ethereum.networkVersion)
				// console.log('networkid: ' + networkId)
				if (config.supportedNetworks[networkId]) {
					return true
				}
				else {
					return false
				}
			}
			else {
				return false
			}
		},

		async checkGeoSupport() {
			this.error.geoSupported = null
			if (config.doGeoChecking) {
				const url = config.backendGeoBlockCheckingURL
				const res = await this.$http.get(url)
				// console.log(res)
				if (res.body && res.body.banned !== undefined) {
					this.geoChecking = res.body
					this.geoBanned = res.body.banned
				}
			}
		},

		async connectWallet() {
			try {
				this.loading = true
				this.error.connectWallet = null
				await window.ethereum.enable()
				this.walletConnected = this.isWalletConnected()
				this.networkSupported = this.isNetworkSupported()
				if (this.walletConnected & this.networkSupported) {
					await this.loadNetwork()
				}
				this.loading = false
			} catch (error) {
				console.error(error)
				this.error.connectWallet = this.isMobile ? this.$t('loginPage.connectWalletError') : this.$t('loginPage.connectMetaMaskError')
				this.loading = false
			}
		},

		toggleAgreement () {
			this.agreeWithTerms = ! this.agreeWithTerms
		},

		async logInBackend () {
			this.loading = true
			this.error.login = null

			await this.loadNetwork()
			// console.log(this.network)

			const url = config.backendLogInURL
			const siginInMessage = config.backendLogInMessage
			const myAddress = this.myAddress
			const networkId = this.network.id

			// Use wallet to get the signature
			let sig = null
			try {
				sig = await this.web3Wrapper.signMessageAsync(myAddress, siginInMessage)
			} catch (err) {
				console.log(err)
				if (err.code && err.code === 4001) {
					this.error.login = this.$t('loginPage.txtAccessDenied')
				}
				else {
					this.error.login = this.$t('loginPage.loginError')
				}
				this.loading = false
				return
			}

			// Log in backend
			const loginData = {
				user: myAddress,
				sig: sig,
				networkid: networkId
			}

			try {
				const res = await this.$http.post(url, loginData)
				// console.log(res)
				const jwtToken = res.body.token
				// console.log(jwtToken)
				localStorage.setItem(config.localStorageKey.login, jwtToken)
				await this.loadTokenInfo(false, res)
				// await this.loadAccount()
				// console.log('Redirect: ' + config.defaultRedirect.appHome)


				const localKeyForBypass = config.localStorageKey.geoBanBypass
				if (localStorage.getItem(localKeyForBypass)) {
					localStorage.removeItem(localKeyForBypass)
				}

				if (this.$route.query && this.$route.query.redirect &&
					!this.$route.query.redirect.toLowerCase().includes('logout') &&
					!this.$route.query.redirect.toLowerCase().includes('login')) {
					const redirectPath = this.$route.query.redirect
					console.log('redirect: ' + redirectPath)
					this.$router.push({ path: redirectPath})
				}
				else {
					// console.log('redirect: ' + config.defaultRedirect.appHome)
					this.$router.push({ name: config.defaultRedirect.appHome })
				}
			} catch (err) {
				console.log(err)
				this.error.login = this.$t('loginPage.loginError')
				return
			} finally {
				this.loading = false
			}
		},
	},
	created () {
		this.init()
	}
}
</script>

<style scoped>
.login-error-section {
	background-color: #FFCCBC;
	padding: 10px;
	font-size: 0.8rem;
	line-height: 1.2rem;
}
</style>
